import { Button } from 'components/common/Button'
import { Icon } from 'components/common/Icon'
import { useCurrencies } from 'contexts/hooks/useCurrencies'
import useTranslation from 'next-translate/useTranslation'
import dynamic from 'next/dynamic'
import { Artwork } from 'utils/data'
import { getArtworkUrl } from 'utils/urls'
import { useOverlay } from '../../contexts'

const Modal = dynamic(
  () => import('components/common/Modal').then(m => m.Modal),
  {
    ssr: false,
  }
)

type Props = {
  type: 'buy' | 'sell'
  price: number
  artwork?: Artwork
}

export const TransactionNotCompletedModal = ({
  type,
  price,
  artwork,
}: Props) => {
  const { t } = useTranslation('common')
  const {
    setIsOpen,
    config: { onClose },
  } = useOverlay()
  const { formatCurrency } = useCurrencies()

  const paragraphPayload = { price: formatCurrency(price) }

  const isSellError = type === 'sell' && artwork

  const closeHandler = () => {
    setIsOpen(false)
    onClose?.()
  }

  return (
    <Modal
      className='relative h-full w-full !max-w-[450px] rounded-lg'
      header={
        <div className='flex items-center space-x-2'>
          <Icon
            name='filled-close'
            className='text-danger-dark'
            filled
            size={24}
          />
          <h5 className='font-body text-xl font-medium text-cool-900'>
            {type === 'buy'
              ? t('order_could_not_be_completed')
              : t('sale_could_not_be_completed')}
          </h5>
        </div>
      }
      body={
        <p className='whitespace-pre-line text-sm text-cool-700' translate='no'>
          {type === 'buy'
            ? t('another_buyer_has_bought', paragraphPayload)
            : t('another_seller_has_sold', paragraphPayload)}
        </p>
      }
      footer={
        <div className='flex justify-end'>
          {isSellError ? (
            <Button
              size='md'
              onClick={closeHandler}
              type='link'
              href={getArtworkUrl(artwork.artists, artwork.name, artwork.id)}
            >
              {t('back_to_artwork_page')}
            </Button>
          ) : (
            <Button size='md' onClick={closeHandler}>
              {t('close')}
            </Button>
          )}
        </div>
      }
      hideCloseButton={Boolean(isSellError)}
    />
  )
}
